import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { WaiterService } from '../../_service/waiter.service';
import { PusherService } from '../../_service/pusher.service';
import { CommonModule } from '@angular/common';
import { NgxPaginationModule } from 'ngx-pagination';
import { BrowserModule } from '@angular/platform-browser';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-revenue',
  standalone: true,
  imports: [
    NgbModule,
    CommonModule,
    NgxPaginationModule,
    FormsModule
  ],
  providers: [DatePipe],
  templateUrl: './revenue.component.html',
  styleUrl: './revenue.component.scss'
})
export class RevenueComponent implements OnInit {
  filter: string = '';
  loading = false; // Main loading state
  refreshing = false; // Background refresh state
  revenueLoading = false; // Revenue cards loading
  transactionsLoading = false; // Transactions table loading

  waiterRevenue: any;
  orders: any[] = [];
  totalAmount: number = 0;
  currentPage: number = 1;
  lastPage: number = 1;
  page: number = 1;
  waiterTransactions: any;
  transactionHistory: any; // Add missing property
  itemsPerPage: any;
  total: any;
  filterType: string = '';
  startDate: string = '';
  endDate: string = '';
  showDatePicker: boolean = false;
  today: string;
  searchTerm: string = '';
  filteredTransactions: any[] = [];

  // Quick filter options
  quickFilters = [
    { key: 'today', label: 'Today', icon: 'fa-calendar-day' },
    { key: 'yesterday', label: 'Yesterday', icon: 'fa-calendar-minus' },
    { key: 'weekly', label: 'This Week', icon: 'fa-calendar-week' },
    { key: 'monthly', label: 'This Month', icon: 'fa-calendar' },
    { key: 'custom', label: 'Custom', icon: 'fa-calendar-alt' }
  ];

  constructor(
    private router: Router,
    private service: WaiterService,
    private pusherService: PusherService,
    private datePipe: DatePipe
  ) {
    this.today = this.datePipe.transform(new Date(), 'yyyy-MM-dd')!;
  }

  ngOnInit(): void {
    this.loadInitialData();
  }

  // Load initial data with main loading state
  loadInitialData() {
    this.loading = true;
    this.fetchWaiterRevenue();
    this.fetchWaiterTransactions();
  }

  // Refresh data in background
  refreshData() {
    this.refreshing = true;
    this.fetchWaiterRevenue();
    this.fetchWaiterTransactions();
  }

  onPageChange(page: number): void {
    this.page = page;
    this.fetchWaiterTransactions();
  }

  fetchWaiterRevenue() {
    this.revenueLoading = true;

    // Build the request object dynamically (only send date if needed)
    const params: any = { filterType: this.filterType };
    if (this.startDate) params.startDate = this.startDate;
    if (this.endDate) params.endDate = this.endDate;

    this.service.fetchWaiterRevenue(params)
      .subscribe({
        next: (res: any) => {
          console.log('Revenue response:', res);
          this.waiterRevenue = res;
        },
        error: (err: any) => {
          console.error('Revenue error:', err);
        },
        complete: () => {
          this.revenueLoading = false;
          this.loading = false;
          this.refreshing = false;
        }
      });
  }

  capitalizeFirstLetter(word: string) {
    if (!word) return '';  // Return an empty string if the input is not valid
    return word.charAt(0).toUpperCase() + word.slice(1);
  }

  fetchTransactionHistory() {
    this.transactionsLoading = true;

    const params: any = { filterType: this.filterType };
    if (this.startDate) params.startDate = this.startDate;
    if (this.endDate) params.endDate = this.endDate;

    this.service.fetchWaiterTransactions(params)
      .subscribe({
        next: (res: any) => {
          console.log('Transaction history response:', res);
          this.transactionHistory = res;
        },
        error: (err: any) => {
          console.error('Transaction history error:', err);
        },
        complete: () => {
          this.transactionsLoading = false;
          this.loading = false;
          this.refreshing = false;
        }
      });
  }

  fetchWaiterTransactions() {
    this.transactionsLoading = true;

    const params: any = {
      page: this.page,
      filterType: this.filterType
    };
    if (this.startDate) params.startDate = this.startDate;
    if (this.endDate) params.endDate = this.endDate;

    this.service.fetchWaiterTransactions(params)
      .subscribe({
        next: (res: any) => {
          console.log('Waiter transactions response:', res);
          this.waiterTransactions = res.order_details.data || res;
          this.filteredTransactions = this.waiterTransactions;
          // Update pagination info if available
          if (res.current_page) this.currentPage = res.current_page;
          if (res.last_page) this.lastPage = res.last_page;
          if (res.total) this.total = res.total;
        },
        error: (err: any) => {
          console.error('Waiter transactions error:', err);
        },
        complete: () => {
          this.transactionsLoading = false;
          this.loading = false;
          this.refreshing = false;
        }
      });
  }

  setDateFilter(filter: string) {
    console.log('Setting filter:', filter);
    this.filterType = filter;
    const today = new Date();

    switch (filter) {
      case 'today':
        this.startDate = this.datePipe.transform(today, 'yyyy-MM-dd')!;
        this.endDate = this.startDate;
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        this.startDate = this.datePipe.transform(yesterday, 'yyyy-MM-dd')!;
        this.endDate = this.startDate;
        break;
      case 'weekly':
        const firstDayOfWeek = new Date(today);
        firstDayOfWeek.setDate(today.getDate() - today.getDay()); // Get Sunday of the current week
        this.startDate = this.datePipe.transform(firstDayOfWeek, 'yyyy-MM-dd')!;
        this.endDate = this.datePipe.transform(today, 'yyyy-MM-dd')!;
        break;
      case 'monthly':
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        this.startDate = this.datePipe.transform(firstDayOfMonth, 'yyyy-MM-dd')!;
        this.endDate = this.datePipe.transform(today, 'yyyy-MM-dd')!;
        break;
      case 'custom':
        this.showDatePicker = true;
        return;
      default:
        this.startDate = '';
        this.endDate = '';
    }

    this.showDatePicker = false;

    console.log('Calling API with:', {
      filterType: this.filterType,
      startDate: this.startDate || 'Not Sent',
      endDate: this.endDate || 'Not Sent'
    });

    this.fetchWaiterRevenue();
    this.fetchWaiterTransactions();
  }

  applyCustomDateFilter(start: string, end: string) {
    if (!start || !end) {
      console.error("Both start and end dates must be selected.");
      return;
    }

    console.log('Applying custom filter:', { start, end });
    this.filterType = 'custom';
    this.startDate = start;
    this.endDate = end;
    this.showDatePicker = false;

    this.fetchWaiterRevenue();
    this.fetchWaiterTransactions();
  }



  onSearch(event: any) {
    this.filterTransactions();
  }

  filterTransactions() {
    if (!this.searchTerm.trim()) {
      this.filteredTransactions = this.waiterTransactions;
      return;
    }

    const searchTermLower = this.searchTerm.toLowerCase().trim();
    this.filteredTransactions = this.waiterTransactions.filter((transaction: any) => {
      return (
        transaction.order_id.toString().toLowerCase().includes(searchTermLower) ||
        transaction.amount.toString().includes(searchTermLower) ||
        transaction.payment_method.toLowerCase().includes(searchTermLower) ||
        transaction.status.toLowerCase().includes(searchTermLower) ||
        new Date(transaction.created_at).toLocaleDateString().toLowerCase().includes(searchTermLower)
      );
    });
  }
}
